"use client";

import { Core } from "@faire/design-tokens";
import { USACertificateFieldsContextProvider } from "@faire/retailer-visitor-shared/features/IDV/resellerCertificateFields";
import { getGlobalInputLabelAndHelper } from "@faire/retailer-visitor-shared/features/IDV/resellerCertificateUtils";
import { isOwfGrantIneligibleStore } from "@faire/retailer-visitor-shared/features/OpenWithFaire/isOwfGrantIneligibleStore";
import { isCountryUSA } from "@faire/retailer-visitor-shared/lib/countryUtils";
import { SignupFormContainer } from "@faire/retailer-visitor-shared/views/SignUp/_components/CommonStyles";
import { SignUpTitle } from "@faire/retailer-visitor-shared/views/SignUp/_components/SignUpTitle";
import { useSignupAndGoToNextStep } from "@faire/retailer-visitor-shared/views/SignUp/_hooks/useSignupAndGoToNextStep";
import { useSignUpUPLT } from "@faire/retailer-visitor-shared/views/SignUp/_hooks/useSignUpUPLT";
import {
  ResellerCertData,
  ResellerCertForm,
  useSchema,
} from "@faire/retailer-visitor-shared/views/SignUp/reseller-cert/__internal__/ResellerCertForm";
import { useGetCountryState } from "@faire/retailer-visitor-shared/views/SignUp/reseller-cert/__internal__/useGetCountryState";
import { useSignUpRequest } from "@faire/retailer-visitor-shared/views/SignUp/SignUpRequestContext";
import { Step } from "@faire/retailer-visitor-shared/views/SignUp/Step";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { IResellerCertificate } from "@faire/web-api--source/indigofair/data/IResellerCertificate";
import { IBusinessIdentifier } from "@faire/web-api--source/indigofair/idv/IBusinessIdentifier";
import { Formik } from "formik";
import React, { useMemo } from "react";

import { IdentityVerificationPolicy } from "../identity-verification/IdentityVerificationPolicy";

export const ResellerCertStep: React.FC = () => {
  const { strictLocalize } = useStrictLocalization();
  const { isLoading } = useGetCountryState();
  const [{ isOwFGrantFlow, country, user, reported_creation_time }] =
    useSignUpRequest([
      "isOwFGrantFlow",
      "country",
      "user",
      "reported_creation_time",
    ]);
  const schema = useSchema(country);
  const initialValues = useInitialValues();
  const isGlobal = !isCountryUSA(country);
  const { signupAndGoToNext, signupError, isSigningUp } =
    useSignupAndGoToNextStep(Step.RESELLER_CERT);
  useSignUpUPLT({ step: Step.RESELLER_CERT, isLoading });

  const shouldSkipSignUp =
    !!isOwFGrantFlow &&
    !isOwfGrantIneligibleStore(reported_creation_time, undefined);

  const handleNext = (values: ResellerCertData) => {
    const {
      certificate_number,
      drivers_license_number,
      ein,
      ssn,
      legal_business_name,
      business_identifier: valueBusinessIdentifier,
    } = values;

    const business_identifier = valueBusinessIdentifier?.identifier_value
      ? IBusinessIdentifier.build({
          ...valueBusinessIdentifier,
        })
      : undefined;

    const shouldSaveResellerCertificate =
      certificate_number || drivers_license_number || ein || ssn;
    const expireTime = values.expires_at
      ? new Date(values.expires_at).getTime()
      : undefined;
    const reseller_certificate = shouldSaveResellerCertificate
      ? IResellerCertificate.build({
          ...omitEmptyValues(values),
          signing_name: `${user?.first_name} ${user?.last_name}`,
          expires_at: expireTime,
        })
      : undefined;

    signupAndGoToNext(
      {
        reseller_certificate,
        legal_business_name,
        business_identifier,
      },
      shouldSkipSignUp
    );
  };

  return (
    <SignupFormContainer>
      <SignUpTitle
        title={
          isGlobal
            ? strictLocalize({
                defaultMessage: "Do you have your business credentials?",
                description: {
                  text: "Title asking the user to provide their business credentials",
                  img: "https://cdn.faire.com/fastly/c5c5bdc915d31e7233086323229eb895cac7ddc865b1b7f85cb44c18835225ec.png",
                },
              })
            : strictLocalize({
                defaultMessage: "Do you have your resale tax ID number?",
                description: {
                  text: "Title asking the user to provide their reseller information",
                  img: "https://cdn.faire.com/fastly/c5c5bdc915d31e7233086323229eb895cac7ddc865b1b7f85cb44c18835225ec.png",
                },
              })
        }
      />
      <Spacer height="1x" />
      <Typography>
        <StrictLocalMsg
          defaultMessage="We use this info to verify retailers and ensure the integrity of our buying community."
          description={{
            text: "Description explaining why we need the reseller ID number",
            img: "https://cdn.faire.com/fastly/c5c5bdc915d31e7233086323229eb895cac7ddc865b1b7f85cb44c18835225ec.png",
          }}
        />
      </Typography>
      <Spacer height="4x" />
      <Formik
        initialValues={initialValues}
        validationSchema={schema}
        onSubmit={handleNext}
        enableReinitialize
      >
        <USACertificateFieldsContextProvider>
          <ResellerCertForm
            loading={isSigningUp || isLoading}
            country={country}
          />
        </USACertificateFieldsContextProvider>
      </Formik>
      <Spacer height="2x" />
      <Typography variant="labelSansRegular" color={Core.text.disabled}>
        <StrictLocalMsg
          defaultMessage="If you don’t have this info now, we may ask for it later."
          description={{
            text: "Description explaining that the user can provide the reseller ID number later if they don't have it now",
            img: "https://cdn.faire.com/fastly/c5c5bdc915d31e7233086323229eb895cac7ddc865b1b7f85cb44c18835225ec.png",
          }}
        />
      </Typography>
      <Spacer height="5x" />
      <IdentityVerificationPolicy />
      {signupError ? (
        <>
          <Spacer height="2x" />
          <Typography color={Core.text.critical}>{signupError}</Typography>
        </>
      ) : null}
    </SignupFormContainer>
  );
};

const useInitialValues = (): ResellerCertData => {
  const [
    {
      country,
      matched_business,
      reseller_certificate,
      legal_business_name,
      business_identifier,
    },
  ] = useSignUpRequest([
    "country",
    "reseller_certificate",
    "matched_business",
    "legal_business_name",
    "business_identifier",
  ]);
  const { strictLocalize } = useStrictLocalization();
  const { countryState } = useGetCountryState();
  const { identifierType } = getGlobalInputLabelAndHelper(
    country,
    strictLocalize
  );
  const expireDate = reseller_certificate?.expires_at
    ? new Date(reseller_certificate.expires_at).toISOString()
    : "";
  const state = reseller_certificate?.state ?? countryState?.state;
  return useMemo(
    () => ({
      certificate_number: reseller_certificate?.certificate_number ?? "",
      drivers_license_number:
        reseller_certificate?.drivers_license_number ?? "",
      ein: reseller_certificate?.ein ?? "",
      ssn: reseller_certificate?.ssn ?? "",
      google_place_id: matched_business?.id,
      state: isCountryUSA(country) ? state : undefined,
      expires_at: expireDate,
      signing_name: reseller_certificate?.signing_name ?? "",
      legal_business_name: legal_business_name ?? "",
      business_identifier:
        business_identifier ??
        IBusinessIdentifier.build({
          identifier_type: identifierType,
          identifier_value: "",
          state: IBusinessIdentifier.IdentifierState.VALID,
        }),
    }),
    [
      business_identifier,
      country,
      expireDate,
      identifierType,
      legal_business_name,
      matched_business?.id,
      reseller_certificate,
      state,
    ]
  );
};

const omitEmptyValues = (values: ResellerCertData) => {
  return Object.entries(values).reduce((acc, [key, value]) => {
    if (value === "" || value === undefined) {
      return acc;
    }
    return { ...acc, [key]: value };
  }, {});
};
